import React, { useState, useEffect, useRef } from 'react';

function SimpleCaptcha({ onValidate, onChange }) {
  const [captchaText, setCaptchaText] = useState('');
  const [userInput, setUserInput] = useState('');
  const [isValid, setIsValid] = useState(false);
  const canvasRef = useRef(null);

  const generateCaptcha = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let result = '';
    for (let i = 0; i < 6; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCaptchaText(result);
    setUserInput('');
    setIsValid(false);
    if (onChange) onChange();
    drawCaptcha(result);
  };

  const drawCaptcha = (text) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Background
    const gradient = ctx.createLinearGradient(0, 0, canvas.width, 0);
    gradient.addColorStop(0, '#e8f0fe');
    gradient.addColorStop(0.5, '#f0f4f8');
    gradient.addColorStop(1, '#e8f0fe');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Draw noise lines
    for (let i = 0; i < 5; i++) {
      ctx.beginPath();
      ctx.moveTo(Math.random() * canvas.width, Math.random() * canvas.height);
      ctx.lineTo(Math.random() * canvas.width, Math.random() * canvas.height);
      ctx.strokeStyle = `rgba(0,0,0,${Math.random() * 0.1 + 0.05})`;
      ctx.stroke();
    }
    
    // Draw text with distortion
    for (let i = 0; i < text.length; i++) {
      const x = 15 + i * 28 + Math.random() * 5;
      const y = 30 + Math.random() * 8;
      const rotation = (Math.random() - 0.5) * 0.4;
      
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(rotation);
      ctx.font = `${28 + Math.random() * 8}px Arial, sans-serif`;
      ctx.fillStyle = `hsl(${200 + Math.random() * 40}, 70%, ${30 + Math.random() * 30}%)`;
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'center';
      ctx.fillText(text[i], 0, 0);
      ctx.restore();
    }
    
    // Draw random dots
    for (let i = 0; i < 30; i++) {
      ctx.fillStyle = `rgba(0,0,0,${Math.random() * 0.2})`;
      ctx.beginPath();
      ctx.arc(Math.random() * canvas.width, Math.random() * canvas.height, 1 + Math.random() * 2, 0, Math.PI * 2);
      ctx.fill();
    }
  };

  useEffect(() => {
    generateCaptcha();
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setUserInput(value);
    
    if (value === captchaText) {
      setIsValid(true);
      if (onValidate) onValidate();
    } else {
      setIsValid(false);
      if (onChange) onChange();
    }
  };

  const handleRefresh = () => {
    generateCaptcha();
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-3">
        <canvas
          ref={canvasRef}
          width={180}
          height={60}
          className="rounded-lg border border-gray-300 cursor-pointer"
          onClick={handleRefresh}
          title="Click to refresh"
        />
        <button
          type="button"
          onClick={handleRefresh}
          className="px-3 py-1.5 text-sm bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
        >
          🔄 Refresh
        </button>
      </div>
      <div className="flex items-center gap-3">
        <input
          type="text"
          value={userInput}
          onChange={handleInputChange}
          placeholder="Enter the code above"
          className={`px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 transition-all w-48 ${
            isValid 
              ? 'border-green-500 ring-green-100 bg-green-50' 
              : userInput 
                ? 'border-red-500 ring-red-100 bg-red-50' 
                : 'border-gray-300 focus:border-green focus:ring-green/10'
          }`}
        />
        {isValid && (
          <span className="text-green-600 text-sm font-medium">✅ Verified</span>
        )}
        {userInput && !isValid && (
          <span className="text-red-500 text-sm font-medium">❌ Incorrect</span>
        )}
      </div>
      <p className="text-xs text-gray-400">Click the image to generate a new code</p>
    </div>
  );
}

export default SimpleCaptcha;
